import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { useParams } from 'react-router-dom';
import i18next from 'i18next';
import { Text } from '@famly/mf_data-display_text';
import { useBreakpoints } from '@famly/mf_util_breakpoints';
import { Stack } from '@famly/mf_layout_stack';
import { Box } from '@famly/mf_layout_box';
import translate from 'signin-app/helpers/translate';
import { accessToken, isPinApp } from 'signin-app/login/selectors';
import { limitedData } from 'signin-app/child/check-out/selectors';
import { PinHeader } from 'signin-app/child/pin-header';
import { PageBodyContainer } from 'signin-app/components/action-buttons';
import PersonSelect from 'signin-app/components/person-select';
import Header from 'signin-app/components/header';
import useTypedSelector from 'signin-app/components/hooks/use-typed-selector';
import { HeaderPerson } from 'signin-app/components/header-person';
import { useWhitelabelConfiguration } from 'signin-app/whitelabel/use-whitelabel-configuration';
import * as EmployeeSelectors from 'signin-app/employee/selectors';
import * as GroupsSelectors from 'signin-app/groups/selectors';
import { ChildOverview } from 'signin-app/child/check-out/child-overview';
import { hasValue } from '@famly/stat_ts-utils_has-value';
import { useQueryParams } from 'web-app/react/hooks/use-query-params';
import { PageContainer } from 'signin-app/components/containers';
import { selectedPickupRelationId } from 'signin-app/child/check-in/selectors';
import { HeaderChild } from 'signin-app/components/header-child';
import { getInitials } from 'signin-app/util/get-initials';
import { useBindDispatch } from 'web-app/react/hooks/use-bind-dispatch';
import { Spinner } from '@famly/mf_feedback_spinner';
import ChildRelations from './child-relations';
import { GoingHomeWith } from './going-home-with';
import PickupTime from './pickup-time';
import { CheckinSubmit } from './checkin-submit';
import { AddPickupPerson } from './add-pickup-person';
import * as ChildSelectors from '../selectors';
import * as Selectors from './selectors';
import * as Actions from './actions';
import * as ChildActions from '../actions';
const ChildInfo = ({ child }) => {
    const isDataLimited = useTypedSelector(limitedData);
    const childName = React.useMemo(() => (isDataLimited ? child.name.firstName : child.name.fullName), [child.name.firstName, child.name.fullName, isDataLimited]);
    const initials = React.useMemo(() => getInitials(childName), [childName]);
    return _jsx(HeaderPerson, { image: child.image.small, initials: initials, name: childName });
};
export const ChildCheckIn = (props) => {
    const { update } = useQueryParams(['update']);
    const isUpdatePage = hasValue(update);
    const pickupTimeRef = React.useRef(null);
    const [shouldScrollDown, setShouldScrollDown] = React.useState(true);
    const { isTabletPortraitAndLarger } = useBreakpoints();
    const isFetching = useTypedSelector(ChildSelectors.isFetching);
    const child = useTypedSelector(state => Selectors.child(state, props));
    const token = useTypedSelector(accessToken);
    const isDataLimited = useTypedSelector(limitedData);
    const canSelectOtherChildsParent = useTypedSelector(state => ChildSelectors.canSelectOtherChildsParent(state, props));
    const { childId: childIdFromParams } = useParams();
    const requirePickupInfo = useTypedSelector(state => ChildSelectors.requirePickupInfo(state, { childId: childIdFromParams }));
    const showPickupInfoScreen = useTypedSelector(state => Selectors.showPickupInfoScreen(state, props));
    const hasExpectedPickupTime = useTypedSelector(GroupsSelectors.hasExpectedPickupTime);
    const pinApp = useTypedSelector(isPinApp);
    const pickupText = !requirePickupInfo ? translate('optionalPickupText') : translate('whoPicksUp');
    const whitelabelConfiguration = useWhitelabelConfiguration();
    const pinChildren = useTypedSelector(ChildSelectors.pinChildren);
    const pinEmployees = useTypedSelector(EmployeeSelectors.pinEmployees);
    const selectedPickupPersonId = useTypedSelector(selectedPickupRelationId);
    const fetchChildAction = useBindDispatch(ChildActions.fetchChild);
    const resetPickupAction = useBindDispatch(Actions.resetPickup);
    React.useEffect(() => {
        if (!pinApp) {
            fetchChildAction(token, childIdFromParams);
        }
        return () => {
            resetPickupAction();
        };
    }, [childIdFromParams, fetchChildAction, pinApp, resetPickupAction, token]);
    // Show child info header only if PersonSelect will not be shown
    const shouldShowChildInfoOnPin = React.useMemo(() => {
        if (!pinApp || (pinEmployees && pinEmployees.size > 0) || (pinChildren && pinChildren.size > 1)) {
            return false;
        }
        return true;
    }, [pinApp, pinChildren, pinEmployees]);
    const scrollDown = React.useCallback(() => {
        if (pickupTimeRef.current && shouldScrollDown) {
            pickupTimeRef.current.scrollIntoView({ behavior: 'smooth' });
            setShouldScrollDown(false);
        }
    }, [shouldScrollDown]);
    if (isFetching || !child) {
        return (_jsxs(PageContainer, { children: [pinApp ? _jsx(PinHeader, { whitelabelConfiguration: whitelabelConfiguration }) : _jsx(Header, {}), _jsx(Stack, { justifyContent: "center", width: "100%", children: _jsx(Spinner, { size: 32, "aria-label": i18next.t($ => $.react.components.loading.spinner.translations.json.loadingAriaLabel) }) })] }));
    }
    return (_jsxs(PageContainer, { children: [pinApp ? (_jsx(PinHeader, { whitelabelConfiguration: whitelabelConfiguration })) : (_jsx(Header, { centerElement: _jsx(HeaderChild, { child: child }) })), _jsx(Box, { children: showPickupInfoScreen || isUpdatePage ? (_jsxs(_Fragment, { children: [_jsxs(PageBodyContainer, { mt: 16, children: [shouldShowChildInfoOnPin && (_jsx(Stack, { justifyContent: "center", mb: 14, children: _jsx(ChildInfo, { child: child }) })), _jsx(PersonSelect, { activeChildId: childIdFromParams }), _jsxs(Stack, { flexDirection: { base: 'column', tabletLandscape: 'row' }, gap: 12, children: [_jsxs(Box, { flex: 1, children: [_jsx(Text, { variant: "h6", color: "n400", emphasized: true, children: pickupText }), _jsxs(ChildRelations, { selectedRelationId: child.pickupRelationId, checkedIn: child.checkedIn, childId: child.id, handleRelationClick: scrollDown, children: [canSelectOtherChildsParent ? _jsx(GoingHomeWith, { child: child }) : null, !pinApp ? (_jsx(AddPickupPerson, { childName: child.name.firstName, isExistingRelationSelected: Boolean(selectedPickupPersonId), size: isTabletPortraitAndLarger ? 'lg' : 'md' })) : null] })] }), hasExpectedPickupTime ? (_jsx(Box, { flex: 1, ref: pickupTimeRef, "data-e2e-id": "expected-pickup-time-picker", children: _jsx(PickupTime, {}) })) : null] })] }), _jsx(CheckinSubmit, { accessToken: token, child: child, isUpdatePage: isUpdatePage })] })) : (_jsx(ChildOverview, { child: child, limitedData: isDataLimited })) })] }));
};
